<template>
    <div v-if="badges.length" class="custom-field-badges">
        <span
            v-for="badge in badges"
            :key="badge.key"
            class="custom-field-badge"
            :style="badge.color ? { backgroundColor: badge.color, color: contrastTextColor(badge.color) } : null"
        >
            {{ badge.text }}
        </span>
    </div>
</template>

<script>
import moment from 'moment';
import { DATE_TYPES } from '@/utils/constants';

export default {
    name: 'CustomFieldBadges',
    props: {
        customFieldDefinitions: {
            type: Array,
            default: () => []
        },
        customFieldValues: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        badges() {
            if (!Array.isArray(this.customFieldDefinitions)) {
                return [];
            }
            return this.customFieldDefinitions
                .filter((def) => def && def.showAsBadge)
                .map((def) => {
                    const value = this.customFieldValues ? this.customFieldValues[def.name] : null;
                    return this.buildBadge(def, value);
                })
                .filter(Boolean);
        }
    },
    methods: {
        buildBadge(def, value) {
            const type = (def.type || '').toLowerCase();
            switch (type) {
                case 'text': {
                    const text = (value || '').toString().trim();
                    if (!text) {
                        return null;
                    }
                    return { key: def.name, text: `${def.label}: ${text}`, color: null };
                }
                case 'date': {
                    if (!value) {
                        return null;
                    }
                    const m = moment(value);
                    if (!m.isValid()) {
                        return null;
                    }
                    return { key: def.name, text: `${def.label}: ${m.format(DATE_TYPES.standardDate)}`, color: null };
                }
                case 'checkbox': {
                    if (!value) {
                        return null;
                    }
                    return { key: def.name, text: def.label, color: null };
                }
                case 'dropdown':
                case 'radiobutton': {
                    if (!value) {
                        return null;
                    }
                    const matched = (def.options || []).find((o) => o.value === value);
                    if (!matched) {
                        return null;
                    }
                    return {
                        key: def.name,
                        text: `${def.label}: ${matched.value}`,
                        color: matched.color || null
                    };
                }
                default:
                    return null;
            }
        },
        contrastTextColor(hex) {
            // Pick black or white text based on the badge background's perceived luminance.
            // Standard relative-luminance formula (sRGB), simplified for hex input.
            const normalized = hex.replace('#', '');
            const expanded =
                normalized.length === 3
                    ? normalized
                        .split('')
                        .map((c) => c + c)
                        .join('')
                    : normalized;
            const r = parseInt(expanded.substr(0, 2), 16);
            const g = parseInt(expanded.substr(2, 2), 16);
            const b = parseInt(expanded.substr(4, 2), 16);
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            return luminance > 0.6 ? '#222' : '#fff';
        }
    }
};
</script>

<style lang="scss" scoped>
.custom-field-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-bottom: 0.5rem;
}
.custom-field-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.75rem;
    line-height: 1.2;
    background: #eceff1;
    color: #455a64;
    white-space: nowrap;
}
</style>
